import React, { useEffect } from 'react';

interface ClosingSummary {
  date: string;
  totalSales: number;
  cashSales: number;
  paypaySales: number;
  transactionCount: number;
  eatinCount: number;
  takeoutCount: number;
}

interface DailyClosingReportHTMLProps {
  summary: ClosingSummary;
  onPrintComplete?: () => void;
}

const DailyClosingReportHTML: React.FC<DailyClosingReportHTMLProps> = ({ summary, onPrintComplete }) => {
  useEffect(() => {
    // iframeを使用してレシート印刷と同じ方式で処理
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
    
    const printWindow = iframe.contentWindow;
    if (!printWindow) {
      console.error('印刷用ウィンドウを開けませんでした');
      if (onPrintComplete) {
        onPrintComplete();
      }
      return;
    }

    const formatCurrency = (amount: number) => {
      return `¥${amount.toLocaleString('ja-JP')}`;
    };

    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      });
    };

    const currentTime = new Date().toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    const averagePrice = summary.transactionCount > 0 
      ? Math.round(summary.totalSales / summary.transactionCount)
      : 0;

    const html = `
<!DOCTYPE html>
<html lang="ja">
<head>
  <meta charset="UTF-8">
  <title>締めレポート - とんかつ和とん</title>
  <style>
    @page {
      size: 80mm auto;
      margin: 0;
    }
    body {
      margin: 0;
      padding: 0;
      font-family: 'MS Gothic', monospace;
      width: 80mm;
      background: white;
    }
    .container {
      padding: 5mm;
      width: 70mm;
      margin: 0 auto;
    }
    .header {
      text-align: center;
      margin-bottom: 5mm;
      border-bottom: 2px solid #000;
      padding-bottom: 3mm;
    }
    .store-name {
      font-size: 20px;
      font-weight: bold;
      margin-bottom: 2mm;
    }
    .report-title {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 2mm;
    }
    .date {
      font-size: 14px;
      margin-bottom: 1mm;
    }
    .time {
      font-size: 12px;
      color: #666;
    }
    .total-section {
      text-align: center;
      padding: 5mm 0;
      border-top: 2px solid #000;
      border-bottom: 2px solid #000;
      margin: 5mm 0;
    }
    .total-amount {
      font-size: 24px;
      font-weight: bold;
      margin-bottom: 2mm;
    }
    .total-label {
      font-size: 14px;
    }
    .details {
      margin: 4mm 0;
    }
    .section {
      margin-bottom: 4mm;
      padding-bottom: 3mm;
      border-bottom: 1px dashed #ccc;
    }
    .section:last-child {
      border-bottom: none;
    }
    .section-title {
      font-size: 14px;
      font-weight: bold;
      margin-bottom: 2mm;
    }
    .row {
      display: flex;
      justify-content: space-between;
      font-size: 14px;
      line-height: 1.8;
    }
    .label {
      flex: 1;
    }
    .value {
      text-align: right;
      font-weight: bold;
    }
    .footer {
      text-align: center;
      margin-top: 5mm;
      padding-top: 3mm;
      border-top: 2px solid #000;
    }
    .footer-text {
      font-size: 14px;
      margin-bottom: 2mm;
    }
    .footer-sub {
      font-size: 12px;
      color: #666;
    }
    .divider {
      text-align: center;
      margin: 3mm 0;
      font-size: 12px;
    }
    .thank-you {
      text-align: center;
      font-size: 14px;
      font-weight: bold;
      margin-top: 5mm;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div class="store-name">とんかつ和とん</div>
      <div class="report-title">日次締めレポート</div>
      <div class="date">${formatDate(summary.date)}</div>
      <div class="time">締め時刻: ${currentTime}</div>
    </div>

    <div class="total-section">
      <div class="total-amount">${formatCurrency(summary.totalSales)}</div>
      <div class="total-label">本日の総売上</div>
    </div>

    <div class="details">
      <div class="section">
        <div class="row">
          <span class="label">取引件数:</span>
          <span class="value">${summary.transactionCount} 件</span>
        </div>
      </div>

      <div class="section">
        <div class="section-title">【支払方法別】</div>
        <div class="row">
          <span class="label">現金:</span>
          <span class="value">${formatCurrency(summary.cashSales)}</span>
        </div>
        <div class="row">
          <span class="label">PayPay:</span>
          <span class="value">${formatCurrency(summary.paypaySales)}</span>
        </div>
      </div>

      <div class="section">
        <div class="section-title">【注文種別】</div>
        <div class="row">
          <span class="label">店内飲食:</span>
          <span class="value">${summary.eatinCount} 件</span>
        </div>
        <div class="row">
          <span class="label">テイクアウト:</span>
          <span class="value">${summary.takeoutCount} 件</span>
        </div>
      </div>

      ${summary.transactionCount > 0 ? `
      <div class="section">
        <div class="section-title">【平均値】</div>
        <div class="row">
          <span class="label">平均客単価:</span>
          <span class="value">${formatCurrency(averagePrice)}</span>
        </div>
      </div>
      ` : ''}
    </div>

    <div class="footer">
      <div class="footer-text">締め作業完了</div>
      <div class="footer-sub">システムを安全に終了できます</div>
    </div>

    <div class="divider">================================</div>
    <div class="thank-you">お疲れ様でした</div>
    <div class="divider">================================</div>
  </div>
</body>
</html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    
    // iframeのロードを待ってから印刷
    iframe.onload = () => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        
        // 印刷完了後の処理
        setTimeout(() => {
          document.body.removeChild(iframe);
          if (onPrintComplete) {
            onPrintComplete();
          }
        }, 1000);
      }, 500);
    };
    
    // クリーンアップ
    return () => {
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };
  }, [summary, onPrintComplete]);

  return null;
};

export default DailyClosingReportHTML;